import React, { useEffect, useRef, useMemo, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import CustomSelect from './CustomSelect';

/**
 * 검색 필터 A 컴포넌트
 * 원본: components/searchFilterA.html
 * 구조: select + input + button
 * 리액트 표준 방식: 화면단에서 전달받은 targetTableId 사용, 정확한 ID 매칭만 사용
 *
 * @example
 * // SearchFilterA 컴포넌트 사용 예시
 * import SearchFilterA from './components/widgets/SearchFilterA';
 * import DataTableA from './components/widgets/DataTableA';
 * import { useMemo } from 'react';
 *
 * const MyPage = () => {
 *   const searchFilterConfig = useMemo(() => ({
 *     id: 'mySearchFilter',
 *     targetTableId: 'myDataTable',
 *     selectOptions: [
 *       { value: 'name', label: '이름' },
 *       { value: 'email', label: '이메일' }
 *     ]
 *   }), []);
 *
 *   const dataTableConfig = useMemo(() => ({
 *     id: 'myDataTable',
 *     apiUrl: '/api/data/list',
 *     params: {},
 *     showTotalCount: true,
 *     columns: [
 *       { key: 'name', label: '이름', align: 'left', width: '30%' },
 *       { key: 'email', label: '이메일', align: 'left', width: '40%' }
 *     ]
 *   }), []);
 *
 *   return (
 *     <div>
 *       <SearchFilterA component={searchFilterConfig} />
 *       <DataTableA component={dataTableConfig} />
 *     </div>
 *   );
 * };
 */
const SearchFilterA = ({ component, data }) => {
  const { t } = useTranslation();
  const comp = component || data;

  const selectOptions = useMemo(() => comp?.selectOptions || [], [comp?.selectOptions]);
  const componentId = useMemo(() => comp?.id || `searchA-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`, [comp?.id]);
  const [searchType, setSearchType] = useState(() => selectOptions?.[0]?.value || '');

  // 옵션이 변경되면 현재 값이 유효한지 확인 후 기본값 보정
  useEffect(() => {
    const list = Array.isArray(selectOptions) ? selectOptions : [];
    const exists = !!searchType && list.some((o) => o?.value === searchType);
    if (!exists) {
      setSearchType(list?.[0]?.value || '');
    }
  }, [selectOptions, searchType]);

  // targetTableId는 단일 문자열 또는 문자열 배열로 받을 수 있음 (여러 테이블 동시 검색 가능)
  // onSearch/onReset만 있으면 DataTable 미연결 단독 검색 모드(상위에서 state로 필터링)이므로 경고 생략
  const targetTableIds = useMemo(() => {
    if (comp?.targetTableId) {
      return Array.isArray(comp.targetTableId) ? comp.targetTableId : [comp.targetTableId];
    }
    if (comp?.onSearch || comp?.onReset) {
      return [];
    }
    console.warn(`[SearchFilterA] targetTableId is required in component prop. Cannot connect to DataTable.`);
    return [];
  }, [comp?.targetTableId, comp?.onSearch, comp?.onReset]);

  const formRef = useRef(null);
  const btnRef = useRef(null);
  const tableInstancesRef = useRef({}); // 여러 테이블 인스턴스를 저장: { tableId: tableInstance }
  const compRef = useRef(comp);

  // comp 변경 시 ref 업데이트
  useEffect(() => {
    compRef.current = comp;
  }, [comp]);

  // 테이블 인스턴스 찾기 (정확한 ID 매칭만 사용)
  const findTableInstance = useCallback((tableId) => {
    if (!tableId) return null;

    const tables = window.__allTables || {};

    // 정확한 ID로만 찾기 (부분 매칭 제거)
    if (tables[tableId]) {
      return tables[tableId];
    }

    return null;
  }, []);

  // 여러 테이블 인스턴스 설정 (리액트 표준 방식)
  const setupTables = useCallback(() => {
    if (!targetTableIds || targetTableIds.length === 0) return;

    // 각 테이블 ID에 대해 인스턴스 찾기
    const findAndStoreTables = () => {
      let allFound = true;
      targetTableIds.forEach(tableId => {
        if (!tableInstancesRef.current[tableId]) {
          const table = findTableInstance(tableId);
          if (table) {
            tableInstancesRef.current[tableId] = table;
          } else {
            allFound = false;
          }
        }
      });
      return allFound;
    };

    // 즉시 확인
    if (findAndStoreTables()) {
      return;
    }

    // 테이블이 등록될 때까지 requestAnimationFrame으로 확인 (리액트 표준 방식)
    let frameCount = 0;
    const maxFrames = 100; // 약 1.6초 (60fps 기준)

    const checkTables = () => {
      frameCount++;
      const allFound = findAndStoreTables();

      if (allFound) {
        return;
      }

      if (frameCount < maxFrames) {
        requestAnimationFrame(checkTables);
      } else {
        // 등록된 테이블 목록 출력 (디버깅)
        const registeredIds = Object.keys(window.__allTables || {});
        const missingIds = targetTableIds.filter(id => !tableInstancesRef.current[id]);
        console.warn(`[SearchFilterA] Some tables not found after ${maxFrames} frames. Missing:`, missingIds, 'Available tables:', registeredIds);
      }
    };

    // DOM이 준비된 후 확인 시작
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        requestAnimationFrame(checkTables);
      }, { once: true });
    } else {
      requestAnimationFrame(checkTables);
    }
  }, [targetTableIds, findTableInstance]);

  // 초기화 핸들러: 검색 필터 항목 모두 최초 상태로 복원 후, 빈 조건으로 필터 검색 1회 수행, 상위 onReset 콜백 호출
  const handleReset = useCallback(async () => {
    const resetSearchType = selectOptions?.[0]?.value ?? '';
    setSearchType(resetSearchType);
    if (formRef.current) {
      const keywordInput = formRef.current.querySelector('input[name="keyword"]');
      if (keywordInput) keywordInput.value = '';
    }
    comp?.onReset?.();
    if (typeof comp?.onSearch === 'function') {
      comp.onSearch({ keyword: '', searchType: resetSearchType });
    }
    if (!targetTableIds?.length) return;

    const resetParams = { keyword: '', searchType: resetSearchType };
    const initPromises = targetTableIds.map(async (tableId) => {
      const table = tableInstancesRef.current[tableId] || findTableInstance(tableId);
      if (table?.setSearchParams) {
        table.setSearchParams(resetParams);
        if (table.init) {
          const r = table.init(1);
          return typeof r?.then === 'function' ? r : Promise.resolve();
        }
      }
      return Promise.resolve();
    });
    await Promise.all(initPromises);
  }, [targetTableIds, findTableInstance, selectOptions, comp]);

  // 검색 핸들러 (리액트 표준 방식) - 여러 테이블에 대해 동시에 검색 (또는 onSearch 콜백만 사용)
  const handleSearch = useCallback(async () => {
    if (!formRef.current) return;

    const fd = new FormData(formRef.current);
    const keyword = fd.get('keyword') ?? '';
    const stFromForm = fd.get('searchType') ?? '';
    /* react-select(CustomSelect)는 name이 FormData에 포함되지 않을 수 있음 → state fallback */
    const effectiveSearchType =
      stFromForm !== null && stFromForm !== undefined && String(stFromForm).trim() !== ''
        ? String(stFromForm)
        : searchType;

    if (typeof compRef.current?.onSearch === 'function') {
      compRef.current.onSearch({ keyword, searchType: effectiveSearchType });
    }

    if (!targetTableIds || targetTableIds.length === 0) return;

    // 각 테이블에 대해 검색 실행
    const searchPromises = targetTableIds.map(async (tableId) => {
      // 테이블 인스턴스가 없으면 다시 시도
      if (!tableInstancesRef.current[tableId]) {
        const table = findTableInstance(tableId);
        if (!table) {
          console.warn(`[SearchFilterA] Table ${tableId} not available for search`);
          return null;
        }
        tableInstancesRef.current[tableId] = table;
      }

      const table = tableInstancesRef.current[tableId];

      // 검색 파라미터 설정 및 초기화
      if (table && table.setSearchParams) {
        table.setSearchParams({ keyword, searchType: effectiveSearchType });
        if (table.init) {
          const initResult = table.init(1);
          if (initResult && typeof initResult.then === 'function') {
            await initResult;
          }
        }
      }

      return table;
    });

    // 모든 테이블 검색이 완료될 때까지 대기
    await Promise.all(searchPromises);
  }, [targetTableIds, findTableInstance, searchType]);

  // 테이블 인스턴스 설정 및 이벤트 바인딩
  useEffect(() => {
    if (!comp) return;
    if (!formRef.current || !btnRef.current) return;

    setupTables();

    const btn = btnRef.current;
    btn.addEventListener('click', handleSearch);

    return () => {
      btn.removeEventListener('click', handleSearch);
      tableInstancesRef.current = {};
    };
  }, [comp, setupTables, handleSearch]);

  if (!comp) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    handleSearch();
  };

  // DOM 구조는 원본과 동일하게 유지, CSS로 반응형 처리
  return (
    <>
      <style>
        {`
          .searchA-form {
            display: flex;
            flex-wrap: nowrap;
            align-items: center;
            gap: 8px;
            width: 100%;
          }

          /* 요소 간 비율 */
          .searchA-select {
            flex: 0 0 18%;  /* 약 1/5 정도 */
            min-width: 200px;
          }
          .searchA-select .searchA-select__control {
            min-height: 40px;
            height: 40px;
            font-size: 14px;
            border-radius: 6px;
          }
          .searchA-select .searchA-select__value-container {
            height: 40px;
            padding: 0 12px;
          }
          .searchA-select .searchA-select__indicators {
            height: 40px;
          }

          .searchA-input {
            flex: 1 1 auto; /* 가변 (주 비율 차지) */
            min-width: 0;
            height: 40px;
            font-size: 14px;
            border-radius: 6px;
            padding: 0 12px;
          }

          .searchA-btn-group {
            display: flex;
            align-items: center;
            gap: 6px;
            flex: 0 0 auto;
          }
          .searchA-btn {
            flex: 1 1 auto;
            min-width: 60px;
            height: 40px;
            border-radius: 6px;
            font-size: 14px;
            display: flex;
            justify-content: center;
            align-items: center;
            white-space: nowrap;
            background: var(--theme-color-secondary);
            border: 1px solid var(--theme-color-secondary);
          }
          .searchA-reset-btn {
            width: 40px;
            height: 40px;
            flex-shrink: 0;
            padding: 0;
            border: 1px solid #d1d5db;
            border-radius: 6px;
            background: #fff;
            color: #6b7280;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;
            transition: border-color 0.2s, background 0.2s;
          }
          .searchA-reset-btn:hover {
            border-color: #9ca3af;
            background: #f9fafb;
          }
          .searchA-reset-btn svg {
            width: 18px;
            height: 18px;
            display: block;
          }

          /* 태블릿: 1줄 유지, 비율만 조정 */
          @media (min-width: 577px) and (max-width: 768px) {
            .searchA-form { gap: 8px; }
            .searchA-select { flex: 0 0 24%; min-width: 90px; }
            .searchA-input { flex: 1 1 auto; min-width: 0; }
            .searchA-btn-group { flex: 0 0 auto; }
            .searchA-reset-btn { width: 40px; height: 40px; flex: 0 0 40px; }
          }

          /* 모바일: 세로 배치, 검색/초기화 버튼 한 줄 */
          @media (max-width: 576px) {
            .searchA-form {
              flex-direction: column;
              align-items: stretch;
              gap: 10px;
            }
            .searchA-select {
              flex: none;
              width: 100%;
              min-width: 0;
            }
            .searchA-select .searchA-select__control,
            .searchA-input {
              width: 100%;
            }
            .searchA-input {
              flex: none;
              min-width: 0;
              box-sizing: border-box;
            }
            .searchA-btn-group {
              flex: none;
              width: 100%;
              justify-content: flex-end;
              gap: 8px;
            }
            .searchA-btn {
              flex: 1 1 auto;
              min-width: 80px;
              height: 44px;
            }
            .searchA-reset-btn {
              flex: 0 0 44px;
              width: 44px;
              height: 44px;
            }
          }
        `}
      </style>
      <section id={componentId} className="searchA-wrapper">
        <form
          ref={formRef}
          className="searchA-form d-flex align-items-center"
          id={`form-${componentId}`}
          onSubmit={handleSubmit}
        >
          {/* 셀렉트 옵션 */}
          <CustomSelect
            name="searchType"
            className="searchA-select"
            classNamePrefix="searchA-select"
            value={searchType}
            options={selectOptions}
            onChange={(v) => setSearchType(v)}
            isSearchable={false}
            isClearable={false}
          />

          {/* 검색어 입력 */}
          <input
            type="text"
            className="form-control searchA-input"
            name="keyword"
            placeholder={comp?.keywordPlaceholder || t('components.common.searchPlaceholder')}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleSearch();
              }
            }}
          />

          {/* 검색 버튼 + 초기화 버튼 */}
          <div className="searchA-btn-group">
            <button
              ref={btnRef}
              type="button"
              id={`btn-${componentId}`}
              className="btn btn-dark searchA-btn"
            >
              {t('components.common.search')}
            </button>
            <button
              type="button"
              className="searchA-reset-btn"
              onClick={handleReset}
              aria-label={t('components.common.reset')}
              title={t('components.common.reset')}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M21 2v6h-6" />
                <path d="M3 12a9 9 0 0 1 15-6.7L21 8" />
                <path d="M3 22v-6h6" />
                <path d="M21 12a9 9 0 0 1-15 6.7L3 16" />
              </svg>
            </button>
          </div>
        </form>
      </section>
    </>
  );
};

export default SearchFilterA;
